import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  Collapse,
  IconButton,
  alpha
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import LearningResultsView from '../../LearningResultsView';
import { touchFriendly, responsiveSpacing } from '../../../styles/responsive';
import { GRADIENTS, SHADOWS } from '../../../styles/theme';
import { AvailableFolders, ParkingTestState } from '../types';

interface ParkingTestPanelProps {
  state: ParkingTestState;
  setState: React.Dispatch<React.SetStateAction<ParkingTestState>>;
  availableFolders: AvailableFolders;
  dataSelectionExpanded: boolean;
  setDataSelectionExpanded: (v: boolean) => void;
  testSettingsExpanded: boolean;
  setTestSettingsExpanded: (v: boolean) => void;
  onStartLearning: () => void;
  projectId: string;
}

export const ParkingTestPanel: React.FC<ParkingTestPanelProps> = ({
  state,
  setState,
  availableFolders,
  dataSelectionExpanded,
  setDataSelectionExpanded,
  testSettingsExpanded,
  setTestSettingsExpanded,
  onStartLearning,
  projectId,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
      {/* 데이터 선택 */}
      <Paper
        className="glass-medium animate-fade-in"
        sx={{
          boxShadow: isDark ? SHADOWS.dark.sm : SHADOWS.light.sm,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ ...responsiveSpacing.cardPadding }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              fontWeight: 600
            }}>
              데이터 선택
            </Typography>
            {isMobile && (
              <IconButton
                onClick={() => setDataSelectionExpanded(!dataSelectionExpanded)}
                sx={{ ...touchFriendly.iconButton }}
              >
                <ExpandMoreIcon
                  sx={{
                    transform: dataSelectionExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                />
              </IconButton>
            )}
          </Box>

          <Collapse in={dataSelectionExpanded}>
            <Box sx={{
              display: 'grid',
              gap: { xs: 2, sm: 3 },
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)'
              }
            }}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>학습 이미지 폴더</InputLabel>
                <Select
                  value={state.selectedLearningFolder}
                  label="학습 이미지 폴더"
                  onChange={(e) => setState(prev => ({ ...prev, selectedLearningFolder: e.target.value }))}
                  sx={{ minHeight: { xs: 44, sm: 56 } }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.action.hover, 0.05)
                          },
                          '&.Mui-selected': {
                            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.15 : 0.1),
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.15)
                            }
                          }
                        }
                      }
                    }
                  }}
                >
                  {availableFolders.learning.map((folder) => (
                    <MenuItem key={folder} value={folder}>
                      {folder}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>ROI 파일</InputLabel>
                <Select
                  value={state.selectedRoiFile}
                  label="ROI 파일"
                  onChange={(e) => setState(prev => ({ ...prev, selectedRoiFile: e.target.value }))}
                  sx={{ minHeight: { xs: 44, sm: 56 } }}
                >
                  {availableFolders.roi.map((file) => (
                    <MenuItem key={file} value={file}>
                      {file}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>테스트 이미지 폴더</InputLabel>
                <Select
                  value={state.selectedTestFolder}
                  label="테스트 이미지 폴더"
                  onChange={(e) => setState(prev => ({ ...prev, selectedTestFolder: e.target.value }))}
                  sx={{ minHeight: { xs: 44, sm: 56 } }}
                >
                  {availableFolders.test.map((folder) => (
                    <MenuItem key={folder} value={folder}>
                      {folder}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Collapse>
        </Box>
      </Paper>

      {/* 테스트 설정 */}
      <Paper
        className="glass-medium animate-fade-in"
        sx={{
          boxShadow: isDark ? SHADOWS.dark.sm : SHADOWS.light.sm,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ ...responsiveSpacing.cardPadding }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              fontWeight: 600
            }}>
              테스트 설정
            </Typography>
            {isMobile && (
              <IconButton
                onClick={() => setTestSettingsExpanded(!testSettingsExpanded)}
                sx={{ ...touchFriendly.iconButton }}
              >
                <ExpandMoreIcon
                  sx={{
                    transform: testSettingsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                />
              </IconButton>
            )}
          </Box>

          <Collapse in={testSettingsExpanded}>
            <Box sx={{
              display: 'grid',
              gap: { xs: 2, sm: 3 },
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)'
              },
              mb: 3
            }}>
              <TextField
                label="Var Threshold"
                type="number"
                value={state.varThreshold}
                onChange={(e) => setState(prev => ({ ...prev, varThreshold: Number(e.target.value) }))}
                fullWidth
                size={isMobile ? "small" : "medium"}
                sx={{ minHeight: { xs: 44, sm: 56 } }}
              />
              <TextField
                label="Learning Rate"
                type="number"
                value={state.learningRate}
                onChange={(e) => setState(prev => ({ ...prev, learningRate: Number(e.target.value) }))}
                fullWidth
                size={isMobile ? "small" : "medium"}
                inputProps={{ step: 0.0001 }}
                sx={{ minHeight: { xs: 44, sm: 56 } }}
              />
              <TextField
                label="Iterations"
                type="number"
                value={state.iterations}
                onChange={(e) => setState(prev => ({ ...prev, iterations: Number(e.target.value) }))}
                fullWidth
                size={isMobile ? "small" : "medium"}
                sx={{ minHeight: { xs: 44, sm: 56 } }}
              />
            </Box>

            <Button
              variant="contained"
              onClick={onStartLearning}
              disabled={state.loading || !state.selectedLearningFolder || !state.selectedRoiFile || !state.selectedTestFolder}
              fullWidth={isMobile}
              startIcon={<PlayIcon />}
              className="hover-lift"
              sx={{
                ...touchFriendly.button,
                background: GRADIENTS.primary,
                fontSize: { xs: '1rem', sm: '1.125rem' },
                py: { xs: 1.5, sm: 2 },
                minWidth: { xs: 'auto', sm: 200 },
                '&:hover': {
                  boxShadow: isDark ? SHADOWS.dark.md : SHADOWS.light.md
                }
              }}
            >
              {state.loading ? '학습 중...' : '학습 시작'}
            </Button>
          </Collapse>
        </Box>
      </Paper>

      {/* 학습 결과 */}
      {state.learningResult?.folder_path && state.learningResultsData?.cctv_list && state.learningResultsData.cctv_list.length > 0 && (
        <Paper
          className="glass-medium animate-fade-in-up"
          sx={{
            boxShadow: isDark ? SHADOWS.dark.md : SHADOWS.light.md,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ ...responsiveSpacing.cardPadding }}>
            <Typography variant="h6" gutterBottom sx={{
              fontSize: { xs: '1rem', sm: '1.25rem' },
              fontWeight: 600
            }}>
              최신 학습 결과
            </Typography>
            <Box sx={{ mt: { xs: 2, sm: 3 } }}>
              <LearningResultsView
                projectId={projectId}
                folderPath={state.learningResult.folder_path}
                cctvList={state.learningResultsData.cctv_list}
                timestamp={state.learningResultsData.timestamp}
              />
            </Box>
          </Box>
        </Paper>
      )}

      {/* 학습 결과 로딩 실패 시 */}
      {state.learningResult?.folder_path && !state.learningResultsData && !state.loading && (
        <Paper
          className="glass-medium animate-fade-in"
          sx={{
            boxShadow: isDark ? SHADOWS.dark.sm : SHADOWS.light.sm,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ ...responsiveSpacing.cardPadding }}>
            <Alert severity="warning" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              학습은 완료되었지만 결과를 불러오는데 실패했습니다.
              <br />
              폴더 경로: {state.learningResult.folder_path}
            </Alert>
          </Box>
        </Paper>
      )}

      {/* 학습 중 로딩 표시 */}
      {state.loading && (
        <Paper
          className="glass-medium animate-pulse"
          sx={{
            boxShadow: isDark ? SHADOWS.dark.md : SHADOWS.light.md,
            border: '1px solid',
            borderColor: 'primary.main'
          }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: { xs: 3, sm: 4 },
            ...responsiveSpacing.cardPadding
          }}>
            <CircularProgress size={isMobile ? 48 : 60} sx={{ mb: 2 }} />
            <Typography variant={isMobile ? "body1" : "h6"} color="text.secondary" sx={{
              textAlign: 'center',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              fontWeight: 600
            }}>
              학습을 진행하고 있습니다...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{
              mt: 1,
              textAlign: 'center',
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}>
              {isMobile
                ? "완료되면 결과가 표시됩니다."
                : "완료되면 결과가 자동으로 표시됩니다."
              }
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
};
